import type { Currency } from '@chukua/common';
import { formatMoney } from '../../../../../shared/policies/format-money';
import { SourceChips } from '../../trip-timeline/components/SourceChips';
import { clockText, dayText } from '../../trip-timeline/policies/timeline-time.policy';
import { accessorialLabel, STATE_LABELS } from '../policies/accessorial-labels.policy';
import type { AccessorialProposal } from '../trip-accessorials.api';
import { DecisionButtons } from './DecisionButtons';
import { ProposalNotes } from './ProposalNotes';

interface ProposalCardProps {
  proposal: AccessorialProposal;
  timezone: string;
  /** False when the trip is locked or the user may not decide; the server still decides. */
  canDecide: boolean;
  onConfirm: () => void;
  onAdjust: () => void;
  onDrop: () => void;
}

/** One proposal: what is charged and why, the events behind it with their sources, and the closer's decision if there is one. */
export function ProposalCard({ proposal, timezone, canDecide, onConfirm, onAdjust, onDrop }: ProposalCardProps) {
  const label = accessorialLabel(proposal.proposalKey);
  const shown = proposal.decision && proposal.state !== 'stale' ? proposal.decision : proposal;
  const money = (amountMinor: number) => formatMoney({ amountMinor, currency: proposal.currency as Currency });

  return (
    <section aria-label={label} className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-fg">{label}</h3>
          <p className="text-sm text-muted">{proposal.reason}</p>
        </div>
        <span className="rounded-full border border-line px-2 py-0.5 text-xs text-fg">{STATE_LABELS[proposal.state]}</span>
      </div>
      <p className="text-sm text-fg">
        {shown.quantity} × {money(proposal.unitAmountMinor)} = <span className="font-semibold">{money(shown.amountMinor)}</span>
        {proposal.state === 'adjusted' && <span className="text-muted"> (proposed {proposal.quantity})</span>}
      </p>
      <ProposalNotes proposal={proposal} />
      <ul aria-label={`Events behind ${label}`} className="flex flex-col gap-1.5">
        {proposal.evidence.map((event) => (
          <li key={event.eventId} className="flex flex-wrap items-center gap-2 text-sm text-fg">
            <span>{event.eventType.replace(/_/g, ' ')}</span>
            <span className="text-muted">
              {dayText(event.deviceTime, timezone)} {clockText(event.deviceTime, timezone)}
            </span>
            <SourceChips sources={[event]} timezone={timezone} />
          </li>
        ))}
      </ul>
      {canDecide && <DecisionButtons state={proposal.state} onConfirm={onConfirm} onAdjust={onAdjust} onDrop={onDrop} />}
    </section>
  );
}
